import { LayoutGrid, Plus, RefreshCw, Shield, Trash2 } from 'lucide-react';

import KubernetesLogo from '@/assets/ico/vendor/kubernetes.svg';
import DockerLogo from '@/assets/ico/vendor/docker.svg';
import PodmanLogo from '@/assets/ico/vendor/podman.svg';

import { ResourceDetailHeader } from '@@/ResourceDetailHeader/ResourceDetailHeader';
import { ResourceStatBlock } from '@@/ResourceDetailHeader/ResourceStatBlock';
import { ActionBarShell } from '@@/ResourceDetailHeader/ActionBarShell';
import { ActionBarButton } from '@@/ResourceDetailHeader/ActionBarButton';
import { HeaderStats } from '@@/ResourceDetailHeader/HeaderStats';

import { EnvironmentGroup, EnvironmentGroupTypeInfo } from '../types';
import { PlatformBadge } from '../components/PlatformBadge';

interface Props {
  group?: EnvironmentGroup;
  isLoading?: boolean;
  onRefresh?: () => void;
  onAddEnvironments?: () => void;
  onDelete?: () => void;
}

export function GroupHeader({
  group,
  isLoading,
  onRefresh,
  onAddEnvironments,
  onDelete,
}: Props) {
  const actionBar = group ? (
    <ActionBarShell>
      <div className="flex items-center gap-3">
        <ActionBarButton
          icon={RefreshCw}
          onClick={() => onRefresh?.()}
          data-cy="group-header-refresh"
        >
          Refresh
        </ActionBarButton>
        <ActionBarButton
          icon={Plus}
          onClick={() => onAddEnvironments?.()}
          data-cy="group-header-add-environments"
        >
          Add environments
        </ActionBarButton>
      </div>
      <div className="flex items-center gap-1">
        <ActionBarButton
          icon={Trash2}
          onClick={() => onDelete?.()}
          data-cy="group-header-delete"
          color="dangerlight"
        >
          Delete
        </ActionBarButton>
      </div>
    </ActionBarShell>
  ) : undefined;

  return (
    <ResourceDetailHeader
      isLoading={isLoading}
      icon={
        <LayoutGrid className="!text-group-accent-8 th-dark:!text-group-accent-2" />
      }
      subtitleLabel="Environment Group"
      title={group?.Name || ''}
      badge={group && <PlatformBadge group={group} />}
      description={group?.Description}
      rightInfo={
        group && (
          <HeaderStats>
            <GroupStatBlocks group={group} />
          </HeaderStats>
        )
      }
      actionBar={actionBar}
    />
  );
}

function GroupStatBlocks({ group }: { group: EnvironmentGroup }) {
  return (
    <>
      <ResourceStatBlock>
        <ResourceStatBlock.Label>Environments</ResourceStatBlock.Label>
        <ResourceStatBlock.Value>
          <PlatformBreakdown group={group} />
        </ResourceStatBlock.Value>
      </ResourceStatBlock>
      <ResourceStatBlock>
        <ResourceStatBlock.Label icon={<Shield className="h-3 w-3" />}>
          Policies
        </ResourceStatBlock.Label>
        <ResourceStatBlock.Value size="lg" align="center">
          {String(group.Policies?.length ?? 0)}
        </ResourceStatBlock.Value>
      </ResourceStatBlock>
    </>
  );
}

const platformLogos: Array<{
  key: keyof EnvironmentGroupTypeInfo;
  logo: string;
  name: string;
}> = [
  { key: 'Kubernetes', logo: KubernetesLogo, name: 'Kubernetes' },
  { key: 'Docker', logo: DockerLogo, name: 'Docker' },
  { key: 'Podman', logo: PodmanLogo, name: 'Podman' },
];

function PlatformBreakdown({ group }: { group: EnvironmentGroup }) {
  if (!group.TypeInfo) {
    const size = group.Total ?? 0;
    return (
      <span className="text-xs font-normal">
        <b className="font-bold">{size}</b>{' '}
        {size === 1 ? 'Environment' : 'Environments'}
      </span>
    );
  }

  const rows = platformLogos.flatMap((platform) => {
    const count = group.TypeInfo?.[platform.key];
    return typeof count === 'number' && count > 0
      ? [{ ...platform, count }]
      : [];
  });

  if (rows.length === 0) {
    return <span className="text-xs font-normal">No environments</span>;
  }

  return (
    <span className="flex flex-col gap-1.5 text-xs font-normal">
      {rows.map((row) => (
        <span key={row.name} className="flex items-center gap-2">
          <img src={row.logo} alt="" className="h-4 w-4" />
          <span>
            <b className="font-bold">{row.count}</b> {row.name}
          </span>
        </span>
      ))}
    </span>
  );
}
